<template>
  <header class="site-header">
    <div class="container">
      <div class="header-inner">
        <!-- Logo -->
        <router-link to="/" class="logo">
          <div class="logo-icon">
            <img src="@/components/images/科曼驰汽车凹陷修复用户icon设计.png" alt="科曼驰" style="width:32px;height:32px"/>
          </div>
          <div class="logo-text">
            <span class="logo-brand">科曼驰</span>
            <span class="logo-en">KOMANCH</span>
          </div>
        </router-link>

        <!-- 导航菜单 -->
        <nav class="main-nav">
          <ul>
            <li><router-link to="/" class="nav-link" active-class="active">首页</router-link></li>
            <li><router-link to="/product" class="nav-link" active-class="active">产品与服务</router-link></li>
            <li><router-link to="/news" class="nav-link" active-class="active">新闻资讯</router-link></li>
            <li><router-link to="/about" class="nav-link" active-class="active">关于我们</router-link></li>
            <li><router-link to="/contact" class="nav-link" active-class="active">联系我们</router-link></li>
          </ul>
        </nav>

        <!-- 右侧操作区 -->
        <div class="header-actions">
          <!-- 已登录：显示用户信息 + 我的订单 + 退出 -->
          <template v-if="isLoggedIn">
            <router-link to="/my-orders" class="btn-myorder">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              我的订单
            </router-link>
            <el-dropdown @command="handleCommand">
              <span class="user-avatar">
                <el-avatar :size="32">{{ userName }}</el-avatar>
                <span class="user-name">{{ userName }}</span>
                <el-icon class="arrow"><ArrowDown /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="logout">
                    <el-icon><SwitchButton /></el-icon>退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </div>

        <!-- 移动端菜单按钮 -->
        <button class="menu-toggle" @click="mobileMenuOpen = !mobileMenuOpen">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>

    <!-- 移动端导航 -->
    <div class="mobile-nav" :class="{ open: mobileMenuOpen }">
      <ul>
        <li><router-link to="/" @click="mobileMenuOpen = false">首页</router-link></li>
        <li><router-link to="/product" @click="mobileMenuOpen = false">产品与服务</router-link></li>
        <li><router-link to="/news" @click="mobileMenuOpen = false">新闻资讯</router-link></li>
        <li><router-link to="/about" @click="mobileMenuOpen = false">关于我们</router-link></li>
        <li><router-link to="/contact" @click="mobileMenuOpen = false">联系我们</router-link></li>
        <li v-if="isLoggedIn" @click="mobileMenuOpen = false"><router-link to="/my-orders">我的订单</router-link></li>
      </ul>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ArrowDown, SwitchButton } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const router = useRouter()
const store = useStore()
const mobileMenuOpen = ref(false)

const isLoggedIn = computed(() => store.getters.isLoggedIn)
const userName = computed(() => store.state.user?.name || store.state.user?.phone?.slice(-4) || '')

const handleCommand = (cmd) => {
  if (cmd === 'logout') handleLogout()
}

const handleLogout = () => {
  store.dispatch('logout')
  ElMessage.success('已退出登录')
  router.push('/')
}

// 滚动时自动收起下拉菜单
const handleScroll = () => {
  if (mobileMenuOpen.value && window.scrollY > 10) {
    mobileMenuOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped lang="scss">
$gold: #d4af37;
$dark: #1a1a1a;
$gray: #6b7280;
$border: #e5e7eb;

.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

.site-header {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.header-inner {
  display: flex; align-items: center; justify-content: space-between; height: 72px;
}

.logo {
  display: flex; align-items: center; gap: 12px; text-decoration: none; flex-shrink: 0;
  .logo-text { display: flex; flex-direction: column; line-height: 1.2;
    .logo-brand { font-size: 20px; font-weight: 700; color: $dark; letter-spacing: 2px; }
    .logo-en    { font-size: 11px; font-weight: 400; color: $gray; letter-spacing: 3px; }
  }
}

.main-nav ul { display: flex; gap: 8px; list-style: none;
  li .nav-link {
    padding: 8px 20px; font-size: 14px; font-weight: 500; color: $dark;
    text-decoration: none; border-radius: 8px; transition: all 0.3s;
    &::after { content: ''; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 0; height: 2px; background: $gold; transition: width 0.3s; }
    &:hover { color: $gold; &::after { width: 60%; } }
    &.active { color: $gold; &::after { width: 60%; } }
    position: relative;
  }
}

.header-actions { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }

.btn-myorder {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; font-size: 13px; font-weight: 500;
  color: $dark; background: transparent;
  border: 1px solid $border; border-radius: 8px;
  text-decoration: none; cursor: pointer; transition: all 0.3s;
  &:hover { border-color: $gold; color: $gold; }
}
.btn-myorder { display: flex; align-items: center; gap: 6px; }

.user-avatar {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 8px; border-radius: 8px; cursor: pointer; transition: background 0.3s;
  &:hover { background: #f5f5f5; }
  .user-name { font-size: 14px; color: $dark; font-weight: 500; }
  .arrow { font-size: 14px; color: $gray; }
}

.menu-toggle {
  display: none; flex-direction: column; gap: 4px; padding: 4px;
  background: none; border: none; cursor: pointer;
  span { display: block; width: 24px; height: 2px; background: $dark; border-radius: 2px; transition: all 0.3s; }
}

.mobile-nav {
  display: none; padding: 16px 24px 24px; background: #fff; border-top: 1px solid $border;
  ul { list-style: none; li a { display: block; padding: 12px 0; font-size: 15px; color: $dark; text-decoration: none; border-bottom: 1px solid $border; &:last-child { border-bottom: none; } &:hover { color: $gold; } }
      .mobile-login { color: $gold; font-weight: 600; cursor: pointer; }
      .mobile-logout a { color: #f56c6c; } }
  &.open { display: block; }
}

@media (max-width: 1024px) {
  .main-nav { display: none; }
  .menu-toggle { display: flex; }
  .mobile-nav { padding: 8px 16px 14px; }
  .mobile-nav ul li a { padding: 8px 0; font-size: 14px; }
}
</style>
